import React, { useEffect, useRef, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'

import Moment from 'moment'

export const DOCUMENT_CATEGORIES = [
  'Work Status',
  'Encounter Note',
  'Diagnostic Report',
  'Prescription',
  'Billing/Fee Ticket',
  'Billing',
  'Other',
]

export function generateSuggestedDocumentName(category, provider, date) {
  const cat = category && category.trim().length > 0 ? category.trim() : 'Scanned Document'
  const d = date ? Moment(date).format('YYYY-MM-DD') : Moment().format('YYYY-MM-DD')
  if (provider && provider.trim().length > 0) {
    return `${cat} - ${provider.trim()} - ${d}`
  }
  return `${cat} - ${d}`
}

export function resolveDocumentName(providedName, category, provider, date) {
  if (providedName && providedName.trim().length > 0) {
    return providedName.trim()
  }
  return generateSuggestedDocumentName(category, provider, date)
}

const DocumentMetadataModal = ({
  show = false,
  onHide,
  onConfirm,
  initialCategory = 'Work Status',
  associatedProviderName = '',
  associatedProviderId = null,
  appointmentId = null,
  providers = [],
  initialDate = null,
  title = 'Document Metadata',
  confirmText = 'Upload & Process HITL',
}) => {
  const [category, setCategory] = useState(initialCategory)
  const [providerName, setProviderName] = useState(associatedProviderName)
  const [providerId, setProviderId] = useState(associatedProviderId)
  const [docDate, setDocDate] = useState(() =>
    initialDate ? Moment(initialDate).format('YYYY-MM-DD') : Moment().format('YYYY-MM-DD'),
  )
  const [docName, setDocName] = useState(() =>
    generateSuggestedDocumentName(initialCategory, associatedProviderName, docDate),
  )
  const [hasUserEditedName, setHasUserEditedName] = useState(false)

  const nameInputRef = useRef(null)

  useEffect(() => {
    if (show) {
      const activeCategory = initialCategory || 'Work Status'
      const activeProvider = associatedProviderName || ''
      const activeDate = initialDate ? Moment(initialDate).format('YYYY-MM-DD') : Moment().format('YYYY-MM-DD')
      setCategory(activeCategory)
      setProviderName(activeProvider)
      setProviderId(associatedProviderId)
      setDocDate(activeDate)
      setDocName(generateSuggestedDocumentName(activeCategory, activeProvider, activeDate))
      setHasUserEditedName(false)
    }
  }, [show, initialCategory, associatedProviderName, associatedProviderId, initialDate])

  const handleCategoryChange = e => {
    const newCat = e.target.value
    setCategory(newCat)
    if (!hasUserEditedName || !docName.trim()) {
      setDocName(generateSuggestedDocumentName(newCat, providerName, docDate))
    }
  }

  const handleProviderSelectChange = e => {
    const selectedId = e.target.value
    setProviderId(selectedId ? parseInt(selectedId, 10) : null)
    const matched = providers.find(p => String(p.id) === String(selectedId))
    const newName = matched ? matched.name || `${matched.firstName || ''} ${matched.lastName || ''}`.trim() : ''
    setProviderName(newName)
    if (!hasUserEditedName || !docName.trim()) {
      setDocName(generateSuggestedDocumentName(category, newName, docDate))
    }
  }

  const handleProviderNameChange = e => {
    const val = e.target.value
    setProviderName(val)
    if (!hasUserEditedName || !docName.trim()) {
      setDocName(generateSuggestedDocumentName(category, val, docDate))
    }
  }

  const handleDateChange = e => {
    const newDate = e.target.value
    setDocDate(newDate)
    if (!hasUserEditedName || !docName.trim()) {
      setDocName(generateSuggestedDocumentName(category, providerName, newDate))
    }
  }

  const handleNameChange = e => {
    setHasUserEditedName(true)
    setDocName(e.target.value)
  }

  const handleSubmit = e => {
    if (e && e.preventDefault) e.preventDefault()
    const resolved = resolveDocumentName(docName, category, providerName, docDate)
    if (onConfirm) {
      onConfirm({
        name: resolved,
        category,
        associatedProviderId: providerId,
        associatedProviderName: providerName,
        documentDate: docDate,
        appointmentId,
      })
    }
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      onEntered={() => {
        // Programmatic focus compliant with zero JSX autoFocus rules
        if (nameInputRef.current) {
          nameInputRef.current.focus()
        }
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group>
            <Form.Label htmlFor="docCategory">Document Category</Form.Label>
            <Form.Control as="select" id="docCategory" value={category} onChange={handleCategoryChange}>
              {DOCUMENT_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          <Form.Group>
            <Form.Label htmlFor="docProvider">Associated Provider</Form.Label>
            {providers && providers.length > 0 ? (
              <Form.Control as="select" id="docProvider" value={providerId || ''} onChange={handleProviderSelectChange}>
                <option value="">-- Select Linked Provider (Optional) --</option>
                {providers.map(p => (
                  <option key={p.id || p.name} value={p.id || ''}>
                    {p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim()}
                  </option>
                ))}
              </Form.Control>
            ) : (
              <Form.Control
                type="text"
                id="docProvider"
                value={providerName}
                onChange={handleProviderNameChange}
                placeholder="Enter provider or clinic name"
              />
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label htmlFor="docName">Document Name</Form.Label>
            <Form.Control
              type="text"
              id="docName"
              ref={nameInputRef}
              value={docName}
              onChange={handleNameChange}
              placeholder="Enter document name"
            />
            <Form.Text className="text-muted">Auto-suggested format: Category - Provider - Date</Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label htmlFor="docDate">Document Date</Form.Label>
            <Form.Control type="date" id="docDate" value={docDate} onChange={handleDateChange} />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" className="submit-scan-btn" onClick={handleSubmit}>
          {confirmText}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default DocumentMetadataModal
